// AdminJobs.tsx — /admin/jobs (Jobs v2, Task 3 of the admin-v2 cycle, spec
// docs/superpowers/specs/2026-08-06-admin-v2-internal-workspaces-design.md §B3). Tabs
// All|Running|Failed with counts (`AdminJobsFilteredDto.counts` is ALWAYS grouped across the
// whole table regardless of the requested filter, per that DTO's doc comment — so one fetch
// backs every tab's badge). Rows link to a job detail PAGE (not a drawer — see
// AdminJobDetail.tsx's doc comment for why) for the full payload/error/retry/cancel actions;
// this table itself keeps a lightweight inline Retry (failed) / Cancel (pending) for the
// common case, same "pending state + inline error" idiom as the v1 failed-jobs table it
// replaces (kept the v1 healthy empty state text for the Failed tab specifically).
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import {
  ADMIN_JOB_STATUS_FILTERS,
  type AdminJobStatusFilter,
} from '@repo/shared-types';
import type {
  AdminCancelJobResponseDto,
  AdminJobsFilteredDto,
  AdminRetryJobResponseDto,
} from '@repo/shared-types';
import { Button } from '@repo/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import { SkeletonText } from '@repo/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@repo/ui/components/table';
import { AdminPagination } from '../components/AdminPagination';
import { apiFetch, ApiError } from '../lib/api';
import { useLocale } from '../locales/context';
import { formatDateTime as formatDateTimeIntl, formatNumber } from '../locales/format';

/** Truncated cell with the full value on `title` (same idiom carried over from v1). */
function TruncatedCell({ value, max = 60 }: { value: string; max?: number }) {
  const truncated = value.length > max ? `${value.slice(0, max)}…` : value;
  return (
    <span className="block max-w-80 truncate" title={value}>
      {truncated}
    </span>
  );
}

function queryKey(status: AdminJobStatusFilter, page: number) {
  return ['admin', 'jobs', status, page] as const;
}

/** Table shape at the real 44px row height (handoff Shell Anatomy §06). */
function TableSkeleton() {
  const widths = ['w-[90%]', 'w-[70%]', 'w-[82%]', 'w-[60%]'];
  return (
    <div className="flex flex-col overflow-hidden rounded-lg border">
      <div className="h-[34px] bg-muted" />
      {widths.map((w, i) => (
        <div key={w} className={`flex h-11 items-center px-4 ${i === 0 ? '' : 'border-t'}`}>
          <SkeletonText className={w} />
        </div>
      ))}
    </div>
  );
}

export function AdminJobs() {
  const { t, locale } = useLocale();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AdminJobStatusFilter>('all');
  const [page, setPage] = useState(1);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [cancelTarget, setCancelTarget] = useState<string | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const { data, isPending, error } = useQuery({
    queryKey: queryKey(status, page),
    queryFn: () =>
      apiFetch<AdminJobsFilteredDto>(`/api/admin/jobs?status=${status}&page=${page}`),
  });

  function selectTab(next: AdminJobStatusFilter) {
    setStatus(next);
    setPage(1);
  }

  function invalidateAllTabs() {
    return queryClient.invalidateQueries({ queryKey: ['admin', 'jobs'] });
  }

  const retryJob = useMutation({
    mutationFn: (id: string) =>
      apiFetch<AdminRetryJobResponseDto>(`/api/admin/jobs/${id}/retry`, { method: 'POST' }),
    onSuccess: (_data, id) => {
      setRowErrors((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      return invalidateAllTabs();
    },
    onError: (err: Error, id) =>
      setRowErrors((prev) => ({
        ...prev,
        [id]: err instanceof ApiError ? err.message : t.admin.jobs.retryJobErrorFallback,
      })),
  });

  const cancelJob = useMutation({
    mutationFn: (id: string) =>
      apiFetch<AdminCancelJobResponseDto>(`/api/admin/jobs/${id}/cancel`, { method: 'POST' }),
    onSuccess: () => {
      setCancelTarget(null);
      return invalidateAllTabs();
    },
    onError: (err) =>
      setCancelError(err instanceof ApiError ? err.message : t.admin.jobs.cancelJobErrorFallback),
  });

  const countFor = (s: 'all' | 'pending' | 'running' | 'done' | 'failed') =>
    data?.counts.find((c) => c.status === s)?.count ?? 0;
  const allCount = data ? data.counts.reduce((sum, c) => sum + c.count, 0) : 0;
  const runningCount = countFor('running');
  const failedCount = countFor('failed');
  const tabCount: Record<AdminJobStatusFilter, number> = {
    all: allCount,
    running: runningCount,
    failed: failedCount,
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="no-scrollbar overflow-x-auto border-b">
        <nav className="flex gap-1 whitespace-nowrap">
          {ADMIN_JOB_STATUS_FILTERS.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => selectTab(tab)}
              className={`-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                status === tab
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t.admin.jobs.tabLabel[tab]} ({formatNumber(tabCount[tab], locale)})
            </button>
          ))}
        </nav>
      </div>

      {isPending && <TableSkeleton />}
      {error && <p className="text-destructive">{error.message}</p>}

      {data && data.items.length === 0 && (
        <p className="text-muted-foreground">
          {status === 'failed' ? t.admin.jobs.noFailedJobs : t.admin.jobs.noJobsToShow}
        </p>
      )}

      {data && data.items.length > 0 && (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t.admin.jobs.colKind}</TableHead>
                <TableHead>{t.admin.jobs.colStatus}</TableHead>
                <TableHead>{t.admin.jobs.colPayload}</TableHead>
                <TableHead>{t.admin.jobs.colAttempts}</TableHead>
                <TableHead>{t.admin.jobs.colCreated}</TableHead>
                <TableHead>{t.admin.jobs.colUpdated}</TableHead>
                <TableHead>{t.admin.jobs.colActions}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((job) => (
                <TableRow key={job.id}>
                  <TableCell className="font-medium">
                    <Link to={`/admin/jobs/${job.id}`} className="hover:underline">
                      {job.kind}
                    </Link>
                  </TableCell>
                  <TableCell>{job.status}</TableCell>
                  <TableCell>
                    <TruncatedCell value={job.payloadSummary} />
                  </TableCell>
                  <TableCell>{job.attempts}</TableCell>
                  <TableCell>{formatDateTimeIntl(job.createdAt, locale)}</TableCell>
                  <TableCell>{formatDateTimeIntl(job.updatedAt, locale)}</TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      {job.status === 'failed' && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={retryJob.isPending && retryJob.variables === job.id}
                          onClick={() => retryJob.mutate(job.id)}
                        >
                          {retryJob.isPending && retryJob.variables === job.id
                            ? t.admin.jobs.retryingCta
                            : t.admin.jobs.retryCta}
                        </Button>
                      )}
                      {job.status === 'pending' && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setCancelError(null);
                            setCancelTarget(job.id);
                          }}
                        >
                          {t.admin.jobs.cancelCta}
                        </Button>
                      )}
                      {rowErrors[job.id] && (
                        <span className="text-xs text-destructive">{rowErrors[job.id]}</span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {data && <AdminPagination meta={data} onPageChange={setPage} />}

      <Dialog open={cancelTarget !== null} onOpenChange={(open) => !open && setCancelTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.admin.jobs.cancelDialogTitle}</DialogTitle>
            <DialogDescription>{t.admin.jobs.cancelDialogDescriptionListRow}</DialogDescription>
          </DialogHeader>
          {cancelError && <p className="text-[13px] text-destructive">{cancelError}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCancelTarget(null)}>
              {t.admin.jobs.keepJobCta}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={cancelJob.isPending}
              onClick={() => cancelTarget && cancelJob.mutate(cancelTarget)}
            >
              {cancelJob.isPending ? t.admin.jobs.cancellingCta : t.admin.jobs.cancelJobCta}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
